import { ICommonContextsProps } from '@ldp/common-contexts';
import { useMemo } from 'react';
import { useDetermineLocale } from './use-determine-locale';
import { trackCartChanges, trackProductCarousel, trackProduct, trackAdEvents } from '@/utils/analytics/analytics-utils';
import { HELIOS_ANALYTICS_EVENT_TYPES } from '@/data/constants';
import { useWindowSize } from '@/hooks/get-screen-width';
import { useSDMCartContext, useSDMCustomerContext } from '@ldp/sdm-contexts';

/**
 * Hook to retrieve the common contexts props.
 */

const { HELIOS_ADD_TO_CART, HELIOS_PRODUCT_CAROUSEL, HELIOS_PRODUCT_EVENT, HELIOS_AD_TRACKING } =
  HELIOS_ANALYTICS_EVENT_TYPES;

export const useCommonContextProps = () => {
  const locale = useDetermineLocale();

  const { currentDevice } = useWindowSize({ isModernized: true });

  const { cartCount, pageId } = useSDMCartContext();
  const { pcOptimumAccount } = useSDMCustomerContext();

  /** The props necessary for the common contexts container */
  const commonContextProps = useMemo<ICommonContextsProps>(
    () => ({
      settings: {
        // ... map app settings data here as per the interface
        locale,
        banner: 'loblaw',
        sduiBaseUrl: 'https://dev-pcexpress.api.loblaw.digital/pcx-bff/api/v1',
        sduiApiKey: 'eZb87pkFiGAB7UAQbQbllgt7WAAk10rf',
        cdnBaseUrl: 'https://assets-platform.loblaws.ca/ldp-core-web-dev/lower/ldp/',
      },
      analytics: {
        trackEvent: (type, context) => {
          const snowplowGlobalContextFields = { cartCount, locale, currentDevice, pageId, pcOptimumAccount };
          switch (type) {
            case HELIOS_ADD_TO_CART:
              trackCartChanges(context, snowplowGlobalContextFields);
              break;
            case HELIOS_PRODUCT_CAROUSEL:
              trackProductCarousel(context, snowplowGlobalContextFields);
              break;
            case HELIOS_PRODUCT_EVENT:
              trackProduct(context, snowplowGlobalContextFields);
              break;
            case HELIOS_AD_TRACKING:
              trackAdEvents(context, snowplowGlobalContextFields);
              break;
            default:
              return;
          }
        },
        trackRender: (componentName, context) => {
          // TBD
        },
      },
      featureFlags: {
        // ... map feature flags data here as per the interface
        EnableTestFeature: true,
        EnableHeliosSnowplow: true,
      },
    }),
    [locale, currentDevice, cartCount, pageId, pcOptimumAccount]
  );

  return {
    locale,
    commonContextProps,
  };
};

export default useCommonContextProps;
